import type { CreateItemData, Item, ItemType, UnitOfMeasure } from '../types/item';

export const ITEM_TYPES: ItemType[] = ['INVENTORY', 'SERVICE', 'NON_INVENTORY'];

export const UNITS_OF_MEASURE: UnitOfMeasure[] = ['UNIT', 'BOX', 'SET', 'PAIR', 'KG', 'LITRE', 'METRE'];

export interface ItemFormData {
  sku: string;
  name: string;
  description: string;
  item_type: ItemType;
  unit_of_measure: UnitOfMeasure;
  unit_price: string;
  standard_cost: string;
  dealer_selling_price: string;
  cash_price: string;
  reorder_point: string;
  taxable: boolean;
  active: boolean;
  income_account_id: string;
  expense_account_id: string;
  asset_account_id: string;
  category_id: string;
}

export const formatItemLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const parsePriceField = (value: string) => {
  if (!value.trim()) return 0;
  const num = parseFloat(value);
  return Number.isFinite(num) && num >= 0 ? num : 0;
};

export const defaultItemFormData = (options?: {
  name?: string;
  itemType?: ItemType;
}): ItemFormData => ({
  sku: '',
  name: options?.name?.trim() ?? '',
  description: '',
  item_type: options?.itemType ?? 'INVENTORY',
  unit_of_measure: 'UNIT',
  unit_price: '0',
  standard_cost: '0',
  dealer_selling_price: '0',
  cash_price: '0',
  reorder_point: '0',
  taxable: false,
  active: true,
  income_account_id: '',
  expense_account_id: '',
  asset_account_id: '',
  category_id: '',
});

export const validateItemForm = (form: ItemFormData): Record<string, string[]> | null => {
  const errors: Record<string, string[]> = {};

  if (!form.sku.trim()) {
    errors.sku = ['SKU is required.'];
  }
  if (!form.name.trim()) {
    errors.name = ['Name is required.'];
  }
  if (!form.description.trim()) {
    errors.description = ['Description is required.'];
  }
  if (!form.category_id) {
    errors.category_id = ['Category is required.'];
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

export const itemToFormData = (item: Item): ItemFormData => ({
  sku: item.sku,
  name: item.name,
  description: item.description ?? '',
  item_type: item.item_type,
  unit_of_measure: item.unit_of_measure,
  unit_price: item.unit_price,
  standard_cost: item.standard_cost ?? '0',
  dealer_selling_price: item.dealer_selling_price ?? '0',
  cash_price: item.cash_price ?? '0',
  reorder_point: item.reorder_point,
  taxable: item.taxable,
  active: item.active,
  income_account_id: item.income_account_id ? String(item.income_account_id) : '',
  expense_account_id: item.expense_account_id ? String(item.expense_account_id) : '',
  asset_account_id: item.asset_account_id ? String(item.asset_account_id) : '',
  category_id: item.category_id ? String(item.category_id) : '',
});

export const canEditItem = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

export const canDeleteItem = () => localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

export const toCreateItemPayload = (form: ItemFormData): CreateItemData => ({
  sku: form.sku.trim(),
  name: form.name.trim(),
  description: form.description.trim(),
  item_type: form.item_type,
  unit_of_measure: form.unit_of_measure,
  unit_price: parsePriceField(form.unit_price),
  standard_cost: parsePriceField(form.standard_cost),
  dealer_selling_price: parsePriceField(form.dealer_selling_price),
  cash_price: parsePriceField(form.cash_price),
  reorder_point: parseFloat(form.reorder_point) || 0,
  taxable: form.taxable,
  active: form.active,
  income_account_id: form.income_account_id ? Number(form.income_account_id) : null,
  expense_account_id: form.expense_account_id ? Number(form.expense_account_id) : null,
  asset_account_id: form.asset_account_id ? Number(form.asset_account_id) : null,
  category_id: Number(form.category_id),
});
